import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { dismissById, getByLoginName } from '../services/suggestions/Suggestions';
import { useApiClient } from './ApiContext';
import { CurrentUserContext } from './CurrentUserContext';

export interface ISuggestion {
  Id: number;
  RuleId: number;
  Title: string;
  Description: string;
  NotificationArea: string;
  Priority: number;
  Dismissed: boolean;
  DismissedNotes?: string;
  CreatedDate: string;
  DismissedDate?: string;
  SpecificDetails?: ISpecificDetails[];
  /** When the rule produced this suggestion (API PascalCase; `suggestiondate` is the legacy spelling). */
  SuggestionDate?: string;
  suggestiondate?: string;
  /** `'Blocked'` locks the home page immediately — see `isBlockingSuggestion`. */
  BlockingScreen?: string | null;
  /** Lock the home page this many calendar days after `SuggestionDate`; null/0 = no day rule. */
  BlockAfterXDays?: number | null;
  [key: string]: any;
}

export interface ISpecificDetails {
  Region: string;
  Details: string;
}

interface SuggestionsContextType {
  suggestions: ISuggestion[];
  suggestionsCount: number;
  suggestionsLoading: boolean;
  suggestionSubmitting: boolean;
  suggestionError: string | null;
  supportMessage: string | null;
  isPanelOpen: boolean;
  panelUrl: string;
  panelSuggestionId: number | null;
  refreshSuggestions: () => Promise<void>;
  dismissSuggestionById: (suggestionId: number, notes: string) => Promise<void>;
  getSuggestionsPage: (skip: number, top: number, notificationArea?: string) => Promise<{ suggestions: ISuggestion[]; total: number }>;
  getSuggestionById: (id: number) => Promise<ISuggestion | null>;
  setSuggestionSubmitting: () => void;
  suggestionFinishedSubmitting: (success: boolean, error: string | null, supportMessage: string | null) => void;
  openPanel: (url: string, suggestionId: number) => void;
  closePanel: () => void;
}

const SuggestionsContext = createContext<SuggestionsContextType>({
  suggestions: [],
  suggestionsCount: 0,
  suggestionsLoading: false,
  suggestionSubmitting: false,
  suggestionError: null,
  supportMessage: null,
  isPanelOpen: false,
  panelUrl: '',
  panelSuggestionId: null,
  refreshSuggestions: async () => {},
  dismissSuggestionById: async () => {},
  getSuggestionsPage: async () => ({ suggestions: [], total: 0 }),
  getSuggestionById: async () => null,
  setSuggestionSubmitting: () => {},
  suggestionFinishedSubmitting: () => {},
  openPanel: () => {},
  closePanel: () => {},
});

interface SuggestionsProviderProps {
  children: ReactNode;
}

const parseSpecificDetails = (suggestions: ISuggestion[]): ISuggestion[] =>
  suggestions.map(s => {
    if (s.SpecificDetails && typeof s.SpecificDetails === 'string') {
      try {
        return { ...s, SpecificDetails: JSON.parse(s.SpecificDetails as unknown as string) };
      } catch {
        return s;
      }
    }
    return s;
  });

export const SuggestionsProvider: React.FC<SuggestionsProviderProps> = ({ children }) => {
  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const [suggestionsCount, setSuggestionsCount] = useState<number>(0);
  const [suggestionsLoading, setSuggestionsLoading] = useState<boolean>(false);
  const [suggestionSubmitting, setSuggestionSubmit] = useState<boolean>(false);
  const [supportMessage, setSupportMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [panelUrl, setPanelUrl] = useState<string>('');
  const [panelSuggestionId, setPanelSuggestionId] = useState<number | null>(null);

  const { currentUser } = useContext(CurrentUserContext);
  const apiClient = useApiClient();

  const refreshSuggestions = useCallback(async () => {
    if (!currentUser?.LoginName) return;

    setSuggestionsLoading(true);
    setError(null);

    try {
      const response = await getByLoginName(currentUser.LoginName, apiClient, {
        orderby: 'SuggestionDate desc',
        count: true,
      });
      const items = parseSpecificDetails(response.data.value || []);
      setSuggestions(items);
      setSuggestionsCount(response.data['@odata.count'] ?? items.length);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while fetching suggestions');
      setSuggestions([]);
      setSuggestionsCount(0);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [currentUser, apiClient]);

  const dismissSuggestionById = useCallback(async (suggestionId: number, notes: string): Promise<void> => {
    if (!currentUser?.LoginName) throw new Error('User not authenticated');
    setSuggestionsLoading(true);
    try {
      await dismissById(apiClient, suggestionId, notes, currentUser.LoginName);
      setSuggestions(prev => prev.filter(s => s.Id !== suggestionId));
      setSuggestionsCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error dismissing suggestion:', err);
      throw err;
    } finally {
      setSuggestionsLoading(false);
    }
  }, [currentUser, apiClient]);

  const getSuggestionsPage = useCallback(async (
    skip: number,
    top: number,
    notificationArea?: string
  ): Promise<{ suggestions: ISuggestion[]; total: number }> => {
    if (!currentUser?.LoginName) return { suggestions: [], total: 0 };
    try {
      const filter = notificationArea ? `NotificationArea eq '${notificationArea}'` : undefined;
      const response = await getByLoginName(currentUser.LoginName, apiClient, {
        skip, top, count: true, orderby: 'SuggestionDate desc', filter,
      });
      return {
        suggestions: parseSpecificDetails(response.data.value || []),
        total: response.data['@odata.count'] ?? 0,
      };
    } catch (err) {
      console.error('Error fetching suggestions page:', err);
      return { suggestions: [], total: 0 };
    }
  }, [currentUser, apiClient]);

  const getSuggestionById = useCallback(async (id: number): Promise<ISuggestion | null> => {
    if (!currentUser?.LoginName) return null;
    try {
      const response = await getByLoginName(currentUser.LoginName, apiClient, {
        filter: `Id eq ${id}`,
        top: 1,
        count: true,
      });
      const items = parseSpecificDetails(response.data.value || []);
      return items[0] ?? null;
    } catch (err) {
      console.error('Error fetching suggestion by id:', err);
      return null;
    }
  }, [currentUser, apiClient]);

  const setSuggestionSubmitting = useCallback(() => {
    setSuggestionSubmit(true);
  }, []);

  const suggestionFinishedSubmitting = useCallback((
    success: boolean,
    error: string | null,
    supportMessage: string | null = null
  ) => {
    setSuggestionSubmit(false);
    setError(error);
    setSupportMessage(supportMessage);
  }, []);

  const openPanel = useCallback((url: string, suggestionId: number) => {
    setPanelUrl(url);
    setPanelSuggestionId(suggestionId);
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    setPanelUrl('');
    setPanelSuggestionId(null);
  }, []);

  useEffect(() => {
    if (currentUser?.LoginName) {
      refreshSuggestions();
    }
  }, [currentUser, refreshSuggestions]);

  const contextValue: SuggestionsContextType = {
    suggestions,
    suggestionsCount,
    suggestionsLoading,
    suggestionSubmitting,
    suggestionError: error,
    supportMessage,
    isPanelOpen,
    panelUrl,
    panelSuggestionId,
    refreshSuggestions,
    dismissSuggestionById,
    getSuggestionsPage,
    getSuggestionById,
    setSuggestionSubmitting,
    suggestionFinishedSubmitting,
    openPanel,
    closePanel,
  };

  return (
    <SuggestionsContext.Provider value={contextValue}>
      {children}
    </SuggestionsContext.Provider>
  );
};

export const useSuggestions = () => {
  const context = useContext(SuggestionsContext);
  if (!context) {
    throw new Error('useSuggestions must be used within a SuggestionsProvider');
  }
  return context;
};

export { SuggestionsContext };
